/**
 * Admin-only: problems a Claude client reported through `survey_report_bug`
 * (V019), and their triage.
 *
 * Untriaged reports sort first, then by priority. Priority and status save on
 * change; the note saves on its own button, since it is typed.
 */
import { useState, type ReactNode } from "react";
import { Bug, ChevronDown, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useBugReports, useTriageBugReport } from "@/lib/useAdmin";
import type { BugReport, BugStatus, BugTriage } from "@/lib/types";
import { cn } from "@/lib/utils";

const IMPACT_LABEL: Record<string, string> = {
  blocked: "Blocked",
  wrong_record: "Wrong record",
  confusing: "Confusing",
  minor: "Minor",
};

const STATUS_LABEL: Record<BugStatus, string> = {
  open: "Open",
  triaged: "Triaged",
  fixed: "Fixed",
  wont_fix: "Won't fix",
  duplicate: "Duplicate",
};

const PRIORITY_LABEL: Record<number, string> = {
  1: "P1 · fix now",
  2: "P2 · next",
  3: "P3 · later",
  4: "P4 · someday",
};

function formatWhen(iso: string | null): string {
  if (!iso) return "";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "" : d.toLocaleString();
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex flex-col gap-0.5">
      <span className="font-sans text-[11px] font-semibold uppercase tracking-wide text-bw-ink-muted">{label}</span>
      <p className="whitespace-pre-wrap font-sans text-xs text-bw-ink">{children}</p>
    </div>
  );
}

const selectClass = "rounded-pill border border-bw-blue-muted/50 bg-transparent px-2 py-1 font-sans text-xs";

function ReportRow({ report }: { report: BugReport }) {
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState(report.triage_note ?? "");
  const triage = useTriageBugReport();
  const closed = !["open", "triaged"].includes(report.status);
  const save = (changes: BugTriage) =>
    triage.mutate({ reportId: report.report_id, changes });

  return (
    <div className={cn("flex flex-col gap-2 border-b border-bw-blue-muted/30 py-3 last:border-0", closed && "opacity-60")}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex min-w-0 flex-col gap-1">
          <span className="flex flex-wrap items-center gap-2 font-sans text-sm font-semibold text-bw-navy">
            {report.summary}
            <span
              className={cn(
                "rounded-pill px-2 py-0.5 text-[10px] font-semibold",
                report.impact === "blocked" || report.impact === "wrong_record"
                  ? "bg-bw-danger/15 text-bw-danger"
                  : "bg-bw-blue-muted/20",
              )}
            >
              {IMPACT_LABEL[report.impact] ?? report.impact}
            </span>
          </span>
          <span className="font-sans text-xs text-bw-ink-muted">
            {report.reference} · {report.reporter_upn ?? report.reporter_oid}
            {report.noticed_by === "assistant" ? " · noticed by Claude" : " · raised by the person"}
            {report.topic_title && <> · {report.topic_title}</>}
            {report.tool && <> · {report.tool}</>}
            {report.created_at && <> · {formatWhen(report.created_at)}</>}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <select
            aria-label="Priority"
            className={selectClass}
            disabled={triage.isPending}
            value={report.priority ?? ""}
            onChange={(e) => save({ priority: e.target.value ? Number(e.target.value) : null })}
          >
            <option value="">Untriaged</option>
            {Object.entries(PRIORITY_LABEL).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
          <select
            aria-label="Status"
            className={selectClass}
            disabled={triage.isPending}
            value={report.status}
            onChange={(e) => save({ status: e.target.value as BugStatus })}
          >
            {Object.entries(STATUS_LABEL).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
          <Button variant="ghost" size="sm" onClick={() => setOpen(!open)}>
            {open ? <ChevronDown className="h-4 w-4" aria-hidden /> : <ChevronRight className="h-4 w-4" aria-hidden />}
            {open ? "Hide" : "Details"}
          </Button>
        </div>
      </div>
      {triage.isError && <span className="font-sans text-xs text-bw-danger">Couldn&apos;t save. Try again.</span>}
      {open && (
        <div className="flex flex-col gap-3 rounded-card border border-bw-blue-muted/40 p-3">
          <Field label="What happened">{report.what_happened}</Field>
          {report.expected && <Field label="Expected">{report.expected}</Field>}
          {report.what_they_said && <Field label="Their words">{report.what_they_said}</Field>}
          {report.mcp_call_id && <Field label="Tool call">{report.mcp_call_id}</Field>}
          <div className="flex flex-col gap-1">
            <label
              className="font-sans text-[11px] font-semibold uppercase tracking-wide text-bw-ink-muted"
              htmlFor={`bug-note-${report.report_id}`}
            >
              Triage note
            </label>
            <div className="flex gap-2">
              <input
                id={`bug-note-${report.report_id}`}
                className="flex-1 rounded-pill border border-bw-blue-muted/50 px-3 py-1.5 font-sans text-sm"
                value={note}
                maxLength={1000}
                placeholder="e.g. duplicate of 3f2a91c0, or the PR that fixes it"
                onChange={(e) => setNote(e.target.value)}
              />
              <Button
                variant="secondary"
                size="sm"
                disabled={triage.isPending || note.trim() === (report.triage_note ?? "")}
                onClick={() => save({ note: note.trim() || null })}
              >
                Save note
              </Button>
            </div>
            {report.triaged_by && (
              <span className="font-sans text-[11px] text-bw-ink-muted">
                Last triaged by {report.triaged_by}
                {report.triaged_at && <> · {formatWhen(report.triaged_at)}</>}
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export function AdminBugReports() {
  const [includeClosed, setIncludeClosed] = useState(false);
  const { data, isLoading, isError } = useBugReports(includeClosed);

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center gap-2 text-base">
          <Bug className="h-4 w-4 text-bw-blue" aria-hidden />
          Bug reports
        </CardTitle>
        <p className="font-sans text-xs text-bw-ink-muted">
          Problems Claude reported with the survey tools, during someone&apos;s interview. Untriaged
          first, then by priority.
        </p>
        <label className="flex w-fit items-center gap-2 font-sans text-xs text-bw-ink-muted">
          <input
            type="checkbox"
            checked={includeClosed}
            onChange={(e) => setIncludeClosed(e.target.checked)}
            className="h-3.5 w-3.5"
          />
          Show fixed, won&apos;t fix and duplicates
        </label>
      </CardHeader>
      <CardContent className="pt-0">
        {isLoading && <p className="font-sans text-sm text-bw-ink-muted">Loading reports…</p>}
        {isError && <p className="font-sans text-sm text-bw-ink-muted">Couldn&apos;t load bug reports.</p>}
        {data && data.length === 0 && <p className="font-sans text-sm text-bw-ink-muted">No reports.</p>}
        {data?.map((report) => <ReportRow key={report.report_id} report={report} />)}
      </CardContent>
    </Card>
  );
}
